import { useState, useEffect } from 'react';

/**
 * Usage:
 *
 * Vanilla JS:
import { wasDismissed, setDismissal } from '@fec/frontend/foundation/client/dismissal-helper.js';

if (!wasDismissed('my-component', 7)) {
  // show component yourself

  myComponent.querySelector('.js-close-button').addEventListener('click', () => {
    setDismissal('my-component');
    // hide component yourself
  }
}
 *
 *
 * React:
import { useDismissible } from '@fec/frontend/foundation/client/dismissal-helper.js';

const MyComponent = () => {
  const { isDismissed, setDismissed } = useDismissible('my-component', 7);

  if (isDismissed) { return null; }

  return (
    <div>
      <h1>My Component</h1>
      <p>This component will not show up for 7 days, if the button is clicked</p>
      <button onClick={setDismissed}>Dismiss</button>
    </div>
  );
};
 */

/**
 * Gets whether a component should still be dismissed based on the stored timestamp.
 * @param {string} key - Unique identifier for the component.
 * @param {number} dismissDays - Number of days the component should stay dismissed.
 * @returns {boolean} - Whether the component should be hidden.
 */
export function wasDismissed(key, dismissDays) {
  const value = localStorage.getItem(key);
  // key doesn't exist (= it wasn't dismissed yet or localstorage was cleared)
  if (!value) {
    return false;
  }

  const dismissedAt = new Date(value).getTime();
  const elapsedTime = Date.now() - dismissedAt;

  return elapsedTime < dismissDays * 24 * 60 * 60 * 1000;
}

/**
 * Stores the dismissal timestamp for a component.
 * @param {string} key - Unique identifier for the component.
 */
export function setDismissal(key) {
  localStorage.setItem(key, new Date().toISOString());
}

/**
 * Manage dismissible components - with a hook!
 * @param {string} key - Unique identifier for a feature/component/modal/...
 * @param {number} dismissDays - Number of days before resurfacing.
 * @returns {{ isDismissed: boolean, setDismissed: () => void }}
 */
export function useDismissible(key, dismissDays) {
  const [isDismissed, setIsDismissed] = useState(() =>
    wasDismissed(key, dismissDays),
  );

  useEffect(() => {
    setIsDismissed(wasDismissed(key, dismissDays));
  }, [key, dismissDays]);

  const setDismissed = () => {
    setDismissal(key);
    setIsDismissed(true);
  };

  return { isDismissed, setDismissed };
}
